/**
 * Endpoints del acta de recepción — backend REAL (`/api/recepcion/actas…`).
 */

import { apiFetch } from '@/api/http';
import {
  AbrirActaResponseSchema,
  ActaResponseSchema,
  CerrarActaResponseSchema,
  ConfirmarActaResponseSchema,
  ExportRecepcionesResponseSchema,
  MarcaResponseSchema,
  RecuperarActaResponseSchema,
  type AbrirActaResponse,
  type ActaRecepcion,
  type CerrarActaResponse,
  type ConfirmarActaResponse,
  type ExportRecepciones,
  type MarcaResponse,
  type MotivoNoRecibida,
  type RecuperarActaResponse,
  type SobranteActa,
  type SerieActa,
  type StockDestino,
} from '@/api/contracts/recepcion-acta';
import { z } from 'zod';

export interface AbrirActaInput {
  glnId: string | number;
  numeroRemito?: string | null;
  numeroFactura?: string | null;
  /** Desambigua cuando dos emisores comparten el número de documento. */
  glnOrigen?: string | null;
}

export async function abrirActa(input: AbrirActaInput): Promise<AbrirActaResponse> {
  const body: Record<string, unknown> = { glnId: Number(input.glnId) };
  if (input.numeroRemito) body.numeroRemito = input.numeroRemito;
  else if (input.numeroFactura) body.numeroFactura = input.numeroFactura;
  if (input.glnOrigen) body.glnOrigen = input.glnOrigen;
  const data = await apiFetch<unknown>('/recepcion/actas', { method: 'POST', body, auth: true });
  return AbrirActaResponseSchema.parse(data);
}

export async function getActa(actaId: number): Promise<ActaRecepcion> {
  const data = await apiFetch<unknown>(`/recepcion/actas/${actaId}`, { auth: true });
  return ActaResponseSchema.parse(data).acta;
}

export type MarcaInput =
  | { via: 'manual'; detailId: number }
  | {
      via: 'escaneo';
      gtin?: string | null;
      lote?: string | null;
      serie: string;
      /** YYYY-MM-DD */
      vencimiento?: string | null;
      lectura?: string | null;
    };

export async function marcarSerie(actaId: number, marca: MarcaInput): Promise<MarcaResponse> {
  const body: Record<string, unknown> = { via: marca.via };
  if (marca.via === 'manual') {
    body.detailId = marca.detailId;
  } else {
    body.serie = marca.serie;
    if (marca.gtin) body.gtin = marca.gtin;
    if (marca.lote) body.lote = marca.lote;
    if (marca.vencimiento) body.vencimiento = marca.vencimiento;
    if (marca.lectura) body.lectura = marca.lectura.slice(0, 4000);
  }
  const data = await apiFetch<unknown>(`/recepcion/actas/${actaId}/marcas`, {
    method: 'POST',
    body,
    auth: true,
  });
  return MarcaResponseSchema.parse(data);
}

export async function desmarcarSerie(actaId: number, detailId: number): Promise<SerieActa | null> {
  const data = await apiFetch<unknown>(`/recepcion/actas/${actaId}/marcas/${detailId}`, {
    method: 'DELETE',
    auth: true,
  });
  return z.object({ serie: z.unknown().nullable() }).parse(data).serie as SerieActa | null;
}

export async function quitarSobrante(
  actaId: number,
  sobranteId: number,
): Promise<SobranteActa | null> {
  const data = await apiFetch<unknown>(`/recepcion/actas/${actaId}/sobrantes/${sobranteId}`, {
    method: 'DELETE',
    auth: true,
  });
  return z.object({ sobrante: z.unknown().nullable() }).parse(data).sobrante as SobranteActa | null;
}

export async function confirmarActa(
  actaId: number,
  stockDestino: StockDestino,
): Promise<ConfirmarActaResponse> {
  const data = await apiFetch<unknown>(`/recepcion/actas/${actaId}/confirmar`, {
    method: 'POST',
    body: { stockDestino },
    auth: true,
  });
  return ConfirmarActaResponseSchema.parse(data);
}

export interface CerrarActaInput {
  motivo?: MotivoNoRecibida | null;
  detalle?: string | null;
  porSerie?: Array<{ detailId: number; motivo: MotivoNoRecibida; detalle?: string | null }>;
}

export async function cerrarActa(
  actaId: number,
  input: CerrarActaInput,
): Promise<CerrarActaResponse> {
  const body: Record<string, unknown> = {};
  if (input.motivo) body.motivo = input.motivo;
  if (input.detalle) body.detalle = input.detalle;
  if (input.porSerie && input.porSerie.length > 0) body.porSerie = input.porSerie;
  const data = await apiFetch<unknown>(`/recepcion/actas/${actaId}/cerrar`, {
    method: 'POST',
    body,
    auth: true,
  });
  return CerrarActaResponseSchema.parse(data);
}

export async function reabrirSerie(actaId: number, detailId: number): Promise<ActaRecepcion> {
  const data = await apiFetch<unknown>(`/recepcion/actas/${actaId}/series/${detailId}/reabrir`, {
    method: 'POST',
    auth: true,
  });
  return ActaResponseSchema.parse(data).acta;
}

export async function recuperarActa(actaId: number): Promise<RecuperarActaResponse> {
  const data = await apiFetch<unknown>(`/recepcion/actas/${actaId}/recuperar`, {
    method: 'POST',
    auth: true,
  });
  return RecuperarActaResponseSchema.parse(data);
}

export interface ExportRecepcionesFilters {
  /** YYYY-MM-DD */
  dateFrom?: string;
  /** YYYY-MM-DD */
  dateTo?: string;
  /** Lista separada por coma: trazado,parcial,error,anulada */
  estado?: string;
}

export async function exportarRecepciones(
  glnId: string | number,
  filters: ExportRecepcionesFilters = {},
): Promise<ExportRecepciones> {
  const params = new URLSearchParams({ glnId: String(Number(glnId)) });
  if (filters.dateFrom) params.set('dateFrom', filters.dateFrom);
  if (filters.dateTo) params.set('dateTo', filters.dateTo);
  if (filters.estado) params.set('estado', filters.estado);
  const data = await apiFetch<unknown>(`/recepcion/export?${params.toString()}`, { auth: true });
  return ExportRecepcionesResponseSchema.parse(data);
}
